import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, LogOut, Package, Star, Instagram, Target, Tag, Printer, Calculator, BarChart3, Percent } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "oz_admin_token";

const SECTIONS = [
  { to: "/admin/espace-pro", label: "Espace Pro", desc: "Comptes pro, produits, commandes, TVA, factures", Icon: Package, testid: "hub-espace-pro" },
  { to: "/admin/codes-pro", label: "Codes pros -15 %", desc: "Codes personnels, e-mails de bienvenue, campagne", Icon: Tag, testid: "hub-codes-pro" },
  { to: "/admin/bon-de-commande", label: "Bon de commande", desc: "Tarifs pro imprimables, à remplir à la main", Icon: Printer, testid: "hub-bon-de-commande" },
  { to: "/admin/simulation-devis", label: "Simulation de devis", desc: "Calculer et imprimer un devis client", Icon: Calculator, testid: "hub-simulation-devis" },
  { to: "/admin/statistiques", label: "Visibilité et statistiques", desc: "Visites, sources (Google, IA), liens UTM, objectifs", Icon: BarChart3, testid: "hub-statistiques" },
  { to: "/admin/codes-promo", label: "Codes promo", desc: "Créer des codes de remise Pro et packs", Icon: Percent, testid: "hub-codes-promo" },
  { to: "/admin/prospection", label: "Prospection", desc: "Salons, boutiques, grossistes : e-mails, relances, suivi", Icon: Target, testid: "hub-prospection" },
  { to: "/admin/commandes", label: "Commandes", desc: "Paiements, statuts, transactions", Icon: Package, testid: "hub-commandes" },
  { to: "/admin/avis", label: "Avis clients", desc: "Modérer, approuver, rejeter", Icon: Star, testid: "hub-avis" },
  { to: "/admin/instagram", label: "Galerie Instagram", desc: "Publications de la page /instagram", Icon: Instagram, testid: "hub-instagram" },
];

export default function AdminHub() {
  const [input, setInput] = useState("");
  const [authed, setAuthed] = useState(!!localStorage.getItem(LS_KEY));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const login = q.get("login");
    if (!login) return;
    window.history.replaceState({}, "", "/admin");
    setLoading(true);
    axios.post(`${API}/admin/login-link/redeem`, { token: login })
      .then(({ data }) => { localStorage.setItem(LS_KEY, data.token); setAuthed(true); })
      .catch((err) => setError(err.response?.data?.detail || "Lien invalide ou expiré."))
      .finally(() => setLoading(false));
  }, []);
  const connect = async (e) => {
    e.preventDefault();
    const tk = input.trim();
    if (!tk) return;
    setLoading(true);
    setError("");
    try {
      await axios.get(`${API}/admin/orders?status=all`, { headers: { "X-Admin-Token": tk } });
      localStorage.setItem(LS_KEY, tk);
      setAuthed(true);
    } catch (err) {
      setError(err.response?.status === 401 ? "Token administrateur invalide." : "Erreur de connexion.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(LS_KEY);
    setAuthed(false);
    setInput("");
  };

  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-4 py-24">
        <div className="flex items-center gap-2 mb-6">
          <ShieldCheck className="w-6 h-6 text-[var(--oz-gold)]" />
          <h1 className="font-serif text-2xl text-[var(--oz-text)]">Espace administrateur</h1>
        </div>
        <form onSubmit={connect} className="bg-white border border-[var(--oz-border)] rounded-2xl p-6 space-y-4">
          <label className="block text-sm text-[var(--oz-muted)]">Token administrateur</label>
          <input
            data-testid="hub-token-input"
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Collez votre token admin"
            className="w-full rounded-xl border border-[var(--oz-border)] px-4 py-3 text-sm outline-none focus:border-[var(--oz-gold)]"
          />
          {error && <p data-testid="hub-error" className="text-sm text-red-600">{error}</p>}
          <button
            data-testid="hub-connect"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[var(--oz-dark)] text-white font-semibold text-sm hover:bg-black transition-colors disabled:opacity-60"
          >
            {loading ? "Connexion..." : "Se connecter"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-12 md:py-16">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-[var(--oz-gold)]" />
          <h1 className="font-serif text-2xl sm:text-3xl text-[var(--oz-text)]">Espace administrateur</h1>
        </div>
        <button data-testid="hub-logout" onClick={logout} className="inline-flex items-center gap-1.5 text-sm text-[var(--oz-muted)] hover:text-[var(--oz-text)]">
          <LogOut className="w-4 h-4" /> Déconnexion
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        {SECTIONS.map(({ to, label, desc, Icon, testid }) => (
          <Link
            key={to}
            to={to}
            data-testid={testid}
            className="group rounded-2xl bg-white border border-[var(--oz-border)] p-6 hover:border-[var(--oz-gold)] hover:shadow-[0_12px_40px_-20px_rgba(0,0,0,0.3)] transition-all"
          >
            <div className="w-11 h-11 rounded-xl bg-[var(--oz-bg-2)] flex items-center justify-center mb-4 group-hover:bg-[var(--oz-gold)]/15 transition-colors">
              <Icon className="w-5 h-5 text-[var(--oz-gold)]" />
            </div>
            <h2 className="font-semibold text-[var(--oz-text)]">{label}</h2>
            <p className="mt-1 text-sm text-[var(--oz-muted)]">{desc}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
